<template>
  <div class="canvas-viewer">
    <div class="canvas-container">
      <canvas ref="canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { fabric } from 'fabric';

export default {
  name: 'CanvasViewer',
  props: {
    canvasData: {
      type: String,
      default: () => ("")
    },
    currentUnitData: {
      type: Object,
      default: () => ({
        parentFloorNum: '',
        unitNum: ''
      })
    }
  },
  data() {
    return {
      canvas: null,
      currentCanvasJSON: null,
      clickedElementText: '',

    };
  },
  watch: {
    canvasData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0 && this.canvas) {
          this.loadCanvasData(newData);
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {

    this.initCanvas();
    this.setupEventListeners();
  },
  methods: {
    initCanvas() {
      const canvasElement = this.$refs.canvas;
      this.canvas = new fabric.Canvas(canvasElement, {
        backgroundColor: '#f9fafb',
        selection: false,
        allowTouchScrolling: false,
        skipTargetFind: false,
        moveCursor: 'default',
        perPixelTargetFind: true,
        isDrawingMode: false
      });

      this.canvas.forEachObject(function (obj) {
        obj.selectable = false;
        obj.evented = true;
        obj.lockMovementX = true;
        obj.lockMovementY = true;
        obj.lockScalingX = true;
        obj.lockScalingY = true;
        obj.lockRotation = true;
        obj.hasControls = false;
        obj.hasBorders = false;
        obj.hoverCursor = 'pointer';
        obj.editable = false;
      });

      if (this.canvasData && Object.keys(this.canvasData).length > 0) {
        this.loadCanvasData(this.canvasData);
      }
    },

    setupEventListeners() {
      if (!this.canvas) {
        return;
      }

      this.canvas.on('mouse:down', (options) => {
        // 检查矩形重叠的辅助函数
        const isRectOverlapping = (rect1, rect2) => {
          return !(rect1.left + rect1.width < rect2.left ||
            rect2.left + rect2.width < rect1.left ||
            rect1.top + rect1.height < rect2.top ||
            rect2.top + rect2.height < rect1.top);
        };

        // 获取canvas数据
        const canvasJSON = this.canvas.toJSON();
        this.currentCanvasJSON = canvasJSON;

        // 查找文本
        let elementText = '';

        // 递归查找对象中的文本
        const findTextInObject = (obj) => {
          // 文本对象
          if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') {
            return obj.text;
          }
          // 组对象
          else if (obj.type === 'group' && obj.objects) {
            for (let i = 0; i < obj.objects.length; i++) {
              const text = findTextInObject(obj.objects[i]);
              if (text) return text;
            }
          }
          // 其他对象
          else if (obj._objects && obj._objects.length > 0) {
            for (let i = 0; i < obj._objects.length; i++) {
              const text = findTextInObject(obj._objects[i]);
              if (text) return text;
            }
          }

          // 检查对象属性中的文本
          for (const prop in obj) {
            if (obj[prop] && typeof obj[prop] === 'object' && obj[prop].text) {
              return obj[prop].text;
            }
          }

          return '';
        };

        // 检查直接点击的对象
        if (options.target) {
          elementText = findTextInObject(options.target);
        }

        // 遍历所有对象查找文本
        if (!elementText) {
          const pointer = this.canvas.getPointer(options.e);
          const allObjects = this.canvas.getObjects();

          // 查找点击位置下的对象
          for (let i = allObjects.length - 1; i >= 0; i--) {
            const obj = allObjects[i];
            if (obj.containsPoint && obj.containsPoint(pointer)) {
              elementText = findTextInObject(obj);
              if (elementText) break;
            }
          }

          // 查找与点击对象重叠的文本
          if (!elementText && options.target) {
            const clickedObj = options.target;
            const clickedBounds = clickedObj.getBoundingRect();

            for (let i = 0; i < allObjects.length; i++) {
              const obj = allObjects[i];
              if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') {
                const textBounds = obj.getBoundingRect();
                if (isRectOverlapping(clickedBounds, textBounds)) {
                  elementText = obj.text;
                  break;
                }
              }
            }
          }
        }

        this.clickedElementText = elementText || '';

        if (this.clickedElementText) {
          console.log('房间号:', this.clickedElementText);

          let room = {
            floorNum: this.currentUnitData.parentFloorNum,
            unitNum: this.currentUnitData.unitNum,
            roomNum: this.clickedElementText
          }

          this.toSimplifyAcceptance(room)

        }
      });
    },

    // 点击楼号，跳转到对应详情页
    toSimplifyAcceptance(room) {
      this.$router.push('/pages/property/simplifyAcceptance?tab=业务受理&searchType=1&searchValue=' + `${room.floorNum}-${room.unitNum}-${room.roomNum}`)
    },

    loadCanvasData(data) {
      if (!this.canvas || !data) return;

      this.canvas.loadFromJSON(data, () => {
        this.canvas.forEachObject(function (obj) {
          obj.selectable = false;
          obj.evented = true;
          obj.lockMovementX = true;
          obj.lockMovementY = true;
          obj.lockScalingX = true;
          obj.lockScalingY = true;
          obj.lockRotation = true;
          obj.hasControls = false;
          obj.hasBorders = false;
          obj.hoverCursor = 'pointer';
          obj.editable = false;
        });

        this.resizeCanvas();
        this.canvas.renderAll();

        // 重置监听器
        this.setupEventListeners();
      });
    },

    resizeCanvas() {
      if (!this.canvas) return;

      // 获取容器尺寸
      const container = this.$el.querySelector('.canvas-container');
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight || 800;

      this.canvas.setWidth(containerWidth);
      this.canvas.setHeight(containerHeight);

      // 计算缩放比例
      const scaleX = containerWidth / 800;
      const scaleY = containerHeight / 600;
      const scale = Math.min(scaleX, scaleY);

      // 设置画布缩放
      this.canvas.setZoom(scale);

      const canvasWidth = 800 * scale;
      const canvasHeight = 600 * scale;
      const offsetX = (containerWidth - canvasWidth) / 10;
      const offsetY = (containerHeight - canvasHeight) / 10;

      this.canvas.setViewportTransform([
        scale, 0, 0, scale, offsetX, offsetY
      ]);

      // 重新渲染
      this.canvas.renderAll();
    },

    // 获取canvas的JSON数据
    getCanvasJSON() {
      if (!this.canvas) return null;
      return this.canvas.toJSON();
    },

    // 获取最近点击元素的文本内容
    getClickedElementText() {
      return this.clickedElementText;
    }
  }
};
</script>

<style scoped>
.canvas-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.canvas-container {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f9fafb;
  overflow: hidden;
  min-height: 600px;
  position: relative;
}

.canvas-container canvas {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: white;
  max-width: 100%;
  max-height: 100%;
  display: block;
}
</style>